/**
 * In-memory mock store for the static prototype.
 *
 * Holds the seed dataset in memory and (optionally) persists mutations to
 * localStorage so a demo session survives page reloads. Call `resetMockData()`
 * to wipe persisted state and start again from the seed.
 *
 * There is no network and no backend — this is the entire "database".
 */

import { buildSeedData, SeedData } from './seed';

const STORAGE_KEY = 'uc_prototype_mock_db';
const STORAGE_VERSION = 'v41';

// Revive Date-typed fields that JSON.stringify flattened to ISO strings.
const reviveDates = (data: SeedData): SeedData => {
  data.alerts.forEach((alert) => {
    alert.createdAt = new Date(alert.createdAt as unknown as string);
    if (alert.resolvedAt) {
      alert.resolvedAt = new Date(alert.resolvedAt as unknown as string);
    }
  });
  return data;
};

const load = (): SeedData => {
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY}_${STORAGE_VERSION}`);
    if (raw) {
      return reviveDates(JSON.parse(raw));
    }
  } catch {
    // Corrupted or unavailable storage — fall through to a fresh seed.
  }
  const seed = buildSeedData();
  persistData(seed);
  return seed;
};

function persistData(data: SeedData): void {
  try {
    localStorage.setItem(`${STORAGE_KEY}_${STORAGE_VERSION}`, JSON.stringify(data));
  } catch {
    // Storage full or unavailable — keep working from memory only.
  }
}

// The single live dataset for the whole app.
export const db: SeedData = load();

/** Persist the current in-memory dataset to localStorage. */
export const persist = (): void => persistData(db);

/** Wipe persisted state and reload the pristine seed dataset. */
export const resetMockData = (): void => {
  try {
    localStorage.removeItem(`${STORAGE_KEY}_${STORAGE_VERSION}`);
  } catch {
    // ignore
  }
  const fresh = buildSeedData();
  (db as any).users = fresh.users;
  (db as any).formTemplates = fresh.formTemplates;
  (db as any).applications = fresh.applications;
  (db as any).assignments = fresh.assignments;
  (db as any).evaluations = fresh.evaluations;
  (db as any).alerts = fresh.alerts;
  (db as any).files = fresh.files;
  (db as any).evaluatorProfiles = fresh.evaluatorProfiles;
  (db as any).reminders = fresh.reminders;
  (db as any).contestExclusions = fresh.contestExclusions;
  persist();
};

/** Simulate network latency so loading states are exercised. */
export const delay = <T>(value: T, ms = 250): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), ms));

/** Generate a pseudo-unique id for newly created records. */
export const newId = (prefix = 'mock'): string =>
  `${prefix}-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;

// Expose a reset helper on window for easy demo resets from the console.
if (typeof window !== 'undefined') {
  (window as any).__resetPrototypeData = resetMockData;
}
